import React, { useMemo, useState } from 'react';
import { Download, ArrowRight, ArrowUpRight, ArrowDownRight, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { MS_PER_DAY } from '../../../config/constants';
import { MODULES } from '../../../content';
import type { QuestionState, FamilyMasteryState } from '../../../mastery';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { StudentReportCard } from '../components/StudentReportCard';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { downloadCsv } from '../../../utils/csvExport';
import { DjayAvatar } from '../../../mascot';
import { DjayInsightBar } from '../components/DjayInsightBar';
import { getStudentInsights } from '../utils/differentiationInsights';
interface ReportsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  onViewAssessmentResults?: () => void;
}
const PERIODS = [{
  key: 7,
  label: 'week'
}, {
  key: 30,
  label: 'maand'
}, {
  key: 90,
  label: 'trimester'
}, {
  key: 365,
  label: 'jaar'
}];
function getPeriodStats(questionStates: Record<string, QuestionState>, familyMastery: Record<string, FamilyMasteryState>, group: number, periodDays: number) {
  const now = Date.now();
  const periodMs = periodDays * MS_PER_DAY;
  const currentStart = now - periodMs;
  const previousStart = currentStart - periodMs;
  const uniqueDays = new Set<string>();
  for (const state of Object.values(questionStates || {})) {
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        const dateStr = new Date(attempt.timestamp).toISOString().split('T')[0];
        uniqueDays.add(dateStr);
      }
    }
  }
  let currentCorrect = 0;
  let currentTotal = 0;
  let previousCorrect = 0;
  let previousTotal = 0;
  for (const [key, state] of Object.entries(familyMastery || {})) {
    if (!key.includes(`_g${group}`)) continue;
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        currentTotal++;
        if (attempt.isCorrect) currentCorrect++;
      } else if (attempt.timestamp >= previousStart && attempt.timestamp < currentStart) {
        previousTotal++;
        if (attempt.isCorrect) previousCorrect++;
      }
    }
  }
  let trendDelta = 0;
  if (currentTotal >= 3 && previousTotal >= 3) {
    const currentAcc = currentCorrect / currentTotal;
    const previousAcc = previousCorrect / previousTotal;
    trendDelta = Math.round((currentAcc - previousAcc) * 100);
  }
  return {
    daysPracticed: uniqueDays.size,
    trendDelta
  };
}
export function ReportsPage({
  students,
  selectedGroup,
  getModuleMastery
}: ReportsPageProps) {
  const [periodDays, setPeriodDays] = useState<number>(7);
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const {
        daysPracticed,
        trendDelta
      } = getPeriodStats(student.data.questionStates || {}, student.data.familyMastery || {}, selectedGroup, periodDays);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        daysPracticed,
        trendDelta
      };
    });
  }, [students, getModuleMastery, selectedGroup, periodDays]);
  const moduleStats = useMemo(() => {
    return getClassModuleStats(students, selectedGroup, getModuleMastery);
  }, [students, selectedGroup, getModuleMastery]);
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return {
      classAvg: 0,
      activeThisPeriod: 0,
      totalPracticedModules: 0
    };
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    const activeThisPeriod = enrichedStudents.filter(s => s.daysPracticed > 0).length;
    const totalPracticedModules = moduleStats.filter(m => m.practicedCount > 0).length;
    return {
      classAvg,
      activeThisPeriod,
      totalPracticedModules
    };
  }, [enrichedStudents, moduleStats]);
  const activePeriodLabel = PERIODS.find(p => p.key === periodDays)?.label || 'week';
  const handleExportClassListCsv = () => {
    const headers = ['Naam', 'Gemiddelde Beheersing (%)', 'Trend (%)', `Dagen Geoefend (deze ${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak'];
    const rows = enrichedStudents.map(s => {
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'klassenlijst');
  };
  const handleExportSubjectsCsv = () => {
    const headers = ['Vak', 'Klasgemiddelde (%)', 'Leerlingen Geoefend', 'Trend'];
    const rows = moduleStats.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.moduleTitle}"`, m.avgMastery, m.practicedCount, `"${trendText}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'vakken_overzicht');
  };
  const handleExportFullClassReport = () => {
    const dateStr = new Date().toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const headers = ['Naam', 'Gemiddelde (%)', 'Trend (%)', `Dagen Geoefend (${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak', 'Aandachtspunt 1', 'Beheersing 1 (%)', 'Aanbeveling 1', 'Aandachtspunt 2', 'Beheersing 2 (%)', 'Aanbeveling 2'];
    const rows = enrichedStudents.map(s => {
      const insights = getStudentInsights(s.data, selectedGroup, 2);
      const i1 = insights[0];
      const i2 = insights[1];
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`, i1 ? `"${i1.familyName}"` : '', i1 ? i1.mastery : '', i1 ? `"${i1.recommendation}"` : '', i2 ? `"${i2.familyName}"` : '', i2 ? i2.mastery : '', i2 ? `"${i2.recommendation}"` : ''].join(',');
    });
    const csvContent = [`Volledig Klasrapport — Meester Djay`, `Groep: ${selectedGroup}`, `Datum: ${dateStr}`, `Periode: Deze ${activePeriodLabel}`, `Klasgemiddelde: ${summaryStats.classAvg}%`, `Actieve leerlingen: ${summaryStats.activeThisPeriod}/${students.length}`, '', headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, `klasrapport_groep${selectedGroup}`);
  };
  return <PageLayout title="Rapporten" description={`${summaryStats.activeThisPeriod}/${students.length} leerlingen actief deze ${activePeriodLabel}`} data-id="element-7222">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-7223">
        {/* Djay Insight Bar */}
        <DjayInsightBar emotion={summaryStats.classAvg >= 70 ? 'proud' : summaryStats.classAvg >= 50 ? 'happy' : 'encouraging'} message={<>
              De klas heeft een gemiddelde beheersing van{' '}
              {summaryStats.classAvg}%. Er hebben{' '}
              {summaryStats.activeThisPeriod} leerlingen geoefend in de
              afgelopen {activePeriodLabel}.
            </>} data-id="element-7224" />

        {/* Period Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-7225">
          {PERIODS.map(period => {
          const isActive = periodDays === period.key;
          return <button key={period.key} onClick={() => setPeriodDays(period.key)} className={`relative px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'}`} data-id="element-7226">
                {isActive && <motion.div layoutId="activePeriodIndicator" className="absolute inset-0 bg-indigo-600 rounded-full shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-7227" />}
                <span className="relative z-10" data-id="element-7228">Deze {period.label}</span>
              </button>;
        })}
        </div>

        {/* Week-over-week Vergelijking */}
        {enrichedStudents.length > 0 && <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-7229">
            <div className="px-5 py-4 bg-white border-b border-slate-100 flex items-center justify-between" data-id="element-7230">
              <div data-id="element-7231">
                <h2 className="text-lg font-black text-slate-900" data-id="element-7232">
                  Periode vergelijking ({enrichedStudents.length} leerlingen)
                </h2>
                <p className="text-sm text-slate-500" data-id="element-7233">
                  Deze {activePeriodLabel} vs. vorige {activePeriodLabel}
                </p>
              </div>
            </div>
            {(() => {
          const sorted = [...enrichedStudents].sort((a, b) => b.trendDelta - a.trendDelta);
          return <div className="overflow-x-auto" data-id="element-7234">
                  <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-7235">
                    <thead data-id="element-7236">
                      <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7237">
                        <th className="py-3 px-5" data-id="element-7238">Leerling</th>
                        <th className="py-3 px-5" data-id="element-7239">Beheersing</th>
                        <th className="py-3 px-5 text-right" data-id="element-7240">Trend</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-7241">
                      {sorted.map((student, idx) => {
                  const isHigh = student.avgMastery >= 70;
                  const isMedium = student.avgMastery >= 40 && student.avgMastery < 70;
                  return <tr key={student.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-7242">
                            <td className="py-3 px-5 font-bold text-sm text-slate-900" data-id="element-7243">
                              {student.name}
                            </td>
                            <td className="py-3 px-5" data-id="element-7244">
                              <div className="flex items-center gap-3" data-id="element-7245">
                                <span className={`font-black text-sm tabular-nums w-10 ${isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-7246">
                                  {student.avgMastery}%
                                </span>
                                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block" data-id="element-7247">
                                  <div className={`h-full rounded-full ${isHigh ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                            width: `${student.avgMastery}%`
                          }} data-id="element-7248" />
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-5 text-right" data-id="element-7249">
                              <div className="flex justify-end" data-id="element-7250">
                                {student.trendDelta > 0 && <div className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md text-xs font-bold" data-id="element-7251">
                                    <ArrowUpRight className="w-3.5 h-3.5" data-id="element-7252" />+
                                    {student.trendDelta}%
                                  </div>}
                                {student.trendDelta < 0 && <div className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-1 rounded-md text-xs font-bold" data-id="element-7253">
                                    <ArrowDownRight className="w-3.5 h-3.5" data-id="element-7254" />
                                    {student.trendDelta}%
                                  </div>}
                                {student.trendDelta === 0 && <div className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 px-2 py-1 rounded-md text-xs font-bold" data-id="element-7255">
                                    <ArrowRight className="w-3.5 h-3.5" data-id="element-7256" />
                                    Stabiel
                                  </div>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>;
        })()}
          </Card>}

        {/* Per-leerling rapport Section */}
        <StudentReportCard students={enrichedStudents} selectedGroup={selectedGroup} getModuleMastery={getModuleMastery} periodDays={periodDays} data-id="element-7257" />

        {/* Export Section */}
        <div className="space-y-3 pt-4" data-id="element-7258">
          <Button onClick={handleExportFullClassReport} size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-id="element-7259">
            <FileText className="w-4 h-4 mr-2" data-id="element-7260" />
            Volledig Klasrapport Exporteren
          </Button>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-7261">
            <Button onClick={handleExportClassListCsv} variant="outline" size="sm" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-7262">
              <Download className="w-4 h-4 mr-2 text-slate-400" data-id="element-7263" />
              Klassenlijst CSV
            </Button>

            <Button onClick={handleExportSubjectsCsv} variant="outline" size="sm" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-7264">
              <Download className="w-4 h-4 mr-2 text-slate-400" data-id="element-7265" />
              Vakken-overzicht CSV
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>;
}